import { useEffect, useId } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";
import { t } from "ttag";

import { ProtectedInformation } from "@/assets/onboarding/protected-information";
import { useCustomerIoContext } from "@/features/analytics/providers";
import { trackNamePageViewed } from "@/features/analytics/trackers/name-page-viewed";
import { ONBOARDING_ROUTE } from "@/features/onboarding/router";
import { useOnboardingStore } from "@/features/onboarding/stores";
import { User } from "@/shared/domains/users";
import {
  Button,
  Form,
  Input,
  Layout,
  SectionHeader,
  Text,
  UserIcon,
} from "@/shared/ui";
import { calculateBmi } from "@/shared/utils";

export const PersonalDetails = () => {
  const { analytics } = useCustomerIoContext();
  const bmiInputs = useOnboardingStore((state) => state.bmiInputs);

  const missingEligibilityAnswers = useOnboardingStore(
    (state) => state.eligibilityAnswerIds === null,
  );

  const personalDetails = useOnboardingStore((state) => state.personalDetails);
  const setPersonalDetails = useOnboardingStore(
    (state) => state.setPersonalDetails,
  );

  const formId = useId();

  useEffect(() => {
    trackNamePageViewed({ customerIoAnalytics: analytics });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const form = useForm({
    resolver: zodResolver(User.omit({ email: true })),
    mode: "onTouched",
    defaultValues: personalDetails,
  });

  const navigate = useNavigate();

  if (!bmiInputs) {
    return <Navigate to={ONBOARDING_ROUTE.BMI} replace />;
  }

  if (missingEligibilityAnswers) {
    return <Navigate to={ONBOARDING_ROUTE.ELIGIBILITY_QUESTIONS} replace />;
  }

  const handleBack = () => {
    navigate(ONBOARDING_ROUTE.ELIGIBILITY_QUESTIONS);
  };

  const handleNext = form.handleSubmit((values) => {
    setPersonalDetails(values);
    navigate(ONBOARDING_ROUTE.ACCOUNT_CONTACT_DETAILS);
  });

  return (
    <Layout.Content>
      <div className="flex flex-col gap-3">
        <SectionHeader title={t`Your medical review`} />

        <div className="mf-masked mf-listen flex flex-col items-center">
          <Text weight="medium" align="center">
            {t`BMI`}: {calculateBmi(bmiInputs)}
            <br />
            {t`Current Weight`}: {bmiInputs.weightInPounds}lbs
          </Text>
        </div>

        <Text size="large" align="center">
          {t`You are a strong candidate for medical weight loss with a high chance of successful treatment if qualified.`}
        </Text>
      </div>

      <Form.Provider {...form}>
        <form
          id={formId}
          onSubmit={handleNext}
          className="flex w-full flex-col gap-2 sm:max-w-xl"
        >
          <Form.Field
            control={form.control}
            name="firstName"
            render={() => (
              <Form.Item className="mf-masked mf-listen">
                <Form.Label aria-required>{t`First name`}</Form.Label>
                <Form.Control>
                  <Input.Root
                    {...form.register("firstName")}
                    placeholder={t`E.g. John`}
                  >
                    <Input.Slot>
                      <UserIcon />
                    </Input.Slot>
                  </Input.Root>
                </Form.Control>
                <Form.Message compact={false} />
              </Form.Item>
            )}
          />

          <Form.Field
            control={form.control}
            name="lastName"
            render={() => (
              <Form.Item className="mf-masked mf-listen">
                <Form.Label aria-required>{t`Last name`}</Form.Label>
                <Form.Control>
                  <Input.Root
                    {...form.register("lastName")}
                    placeholder={t`E.g. Doe`}
                  >
                    <Input.Slot>
                      <UserIcon />
                    </Input.Slot>
                  </Input.Root>
                </Form.Control>
                <Form.Message compact={false} />
              </Form.Item>
            )}
          />

          <div className="flex flex-col gap-3">
            <Button size="lg" type="submit">
              {t`Continue`}
            </Button>

            <Button variant="secondary" size="lg" onClick={handleBack}>
              {t`Back`}
            </Button>
          </div>
        </form>
      </Form.Provider>

      <ProtectedInformation />
    </Layout.Content>
  );
};
